// A title's badge (Ben, Oct 19 2026), drawn the way the game draws it: the title's grey height map from the back end, the
// tier's ring and the badge material (src/kit/badgeGl.ts, the Unity front end's shader). Freestanding like the troop icons.
// Where it can't be drawn (no WebGL2, no height map), the game's flat coin picture from the catalogue; failing that, the
// crown medallion. `live` (the big view) draws it continuously so the light moves with the phone's tilt, a drag or the
// mouse.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { IconCrown } from "@/kit/icons";
import { Medallion, cx } from "@/kit/primitives";
import { badgePicture, badgePx } from "@/kit/badgeArt";
import { titleArtUrl, titleIconFile } from "@/game/titles";
import { LiveBadge } from "./LiveBadge";
import "../social.css";

export function TitleBadge({ title, tier, details, size, glow, dim, className, live }: {
  title: string;
  tier?: number | null;
  details: Record<string, unknown> | undefined;
  /** in --u units; leave out to size it from CSS (--s) */
  size?: number;
  glow?: boolean;
  dim?: boolean;
  className?: string;
  /** draw it live (the big view): the light follows the tilt */
  live?: boolean;
}) {
  const box = useRef<HTMLSpanElement>(null);
  const [px, setPx] = useState(0);
  const [drawn, setDrawn] = useState<{ key: string; url: string | null } | null>(null);
  const [liveFailed, setLiveFailed] = useState(false);
  const key = `${title}|${tier ?? 1}|${px}`;

  useLayoutEffect(() => {
    const w = box.current?.getBoundingClientRect().width || 52;
    setPx(badgePx(w));
  }, [size, className]);

  useEffect(() => {
    if (!px || (live && !liveFailed)) return;
    let on = true;
    void badgePicture(title, tier, px).then((url) => on && setDrawn({ key, url }));
    return () => {
      on = false;
    };
  }, [title, tier, px, key, live, liveFailed]);

  const style = size ? { ["--s" as string]: `calc(var(--u) * ${size})` } : undefined;
  const cls = cx("tt-badge", glow && "glow", dim && "dim", className);

  if (live && !liveFailed) {
    return (
      <span ref={box} className={cx(cls, "live")} style={style}>
        <LiveBadge title={title} tier={tier} onFail={() => setLiveFailed(true)} />
      </span>
    );
  }
  const mine = drawn?.key === key ? drawn : null;
  if (mine?.url) {
    return (
      <span ref={box} className={cls} style={style}>
        <img src={mine.url} alt="" decoding="async" draggable={false} />
      </span>
    );
  }
  if (!mine) return <span ref={box} className={cx(cls, "wait")} style={style} aria-hidden="true" />;
  return <CoinBadge title={title} tier={tier} details={details} size={size} glow={glow} dim={dim} className={className} />;
}

/** The live app's empty socket (badge_empty.png): where no title is worn, or titles are kept private
 *  (CachedTitleImageLoader.ClearImage shows its placeholder). */
export function EmptyBadge({ size, className }: { size?: number; className?: string }) {
  return <span className={cx("tt-badge empty", className)} style={size ? { ["--s" as string]: `calc(var(--u) * ${size})` } : undefined} aria-hidden="true" />;
}

/** The game's flat coin picture (the catalogue's `icon`), or the crown medallion. */
function CoinBadge({ title, tier, details, size, glow, dim, className }: {
  title: string; tier?: number | null; details: Record<string, unknown> | undefined; size?: number; glow?: boolean; dim?: boolean; className?: string;
}) {
  const src = titleArtUrl(titleIconFile(title, tier, details));
  const [failed, setFailed] = useState<string | null>(null);
  if (!src || failed === src) return <Medallion size={size ?? 52} glow={glow} dim={dim} className={className}><IconCrown /></Medallion>;
  return (
    <span className={cx("tt-badge", glow && "glow", dim && "dim", className)} style={size ? { ["--s" as string]: `calc(var(--u) * ${size})` } : undefined}>
      <img src={src} alt="" loading="lazy" decoding="async" draggable={false} onError={() => setFailed(src)} />
    </span>
  );
}
